import { createFileRoute, Link } from '@tanstack/react-router';
import { useForms, useDeleteForm } from '@wordpuppi/shared';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { confirmDialog } from '@/lib/dialog';
import { useBaxterChanged } from '@/lib/baxterEffects';
import { useTurnHighlight } from '@/lib/useTurnHighlight';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sites/$siteSlug/forms/')({
  component: FormsPage,
});

function FormsPage() {
  const { siteSlug } = Route.useParams();
  const { data, isLoading, error } = useForms(siteSlug);
  const deleteForm = useDeleteForm(siteSlug);
  // `data?.data` and not `forms`: the `?? []` fallback is a fresh array every
  // render, which would look like a change to the snapshot diff.

  const forms = data?.data ?? [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const flash = useTurnHighlight(data?.data?.map((row) => ({ id: row.id, name: row.name, slug: row.slug, date: formatDate(row.created_at), submissions: row.submission_count })), useBaxterChanged('forms'));

  const handleDelete = async (formId: string, formName: string) => {
    try {
      await deleteForm.mutateAsync(formId);
      toast.success(`Deleted "${formName}"`);
    } catch {
      toast.error('Failed to delete form');
    }
  };

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Failed to load forms
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Forms</h1>
          <p className="text-muted-foreground mt-1">
            Create and manage forms for your site
          </p>
        </div>
        <Button asChild>
          <Link to="/sites/$siteSlug/forms/new" params={{ siteSlug }}>
            New Form
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : forms.length === 0 ? (
        <div className="border border-dashed rounded-lg py-16 px-6 text-center">
          <p className="text-lg font-medium">No forms yet</p>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            Build contact forms, surveys, or signups to collect submissions from your site.
          </p>
          <Button variant="outline" asChild className="mt-4">
            <Link to="/sites/$siteSlug/forms/new" params={{ siteSlug }}>
              Create your first form
            </Link>
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Submissions</TableHead>
                <TableHead className="w-24 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {forms.map((form) => (
                <TableRow
                  key={form.id}
                  data-changed={flash.has(form.id) ? 'true' : undefined}
                  className={cn(flash.has(form.id) && 'baxter-changed')}
                >
                  <TableCell className="font-medium">
                    <Link
                      to="/sites/$siteSlug/forms/$formId"
                      params={{ siteSlug, formId: form.id }}
                      className="text-foreground hover:underline"
                    >
                      {form.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground font-mono text-sm">
                    {form.slug}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(form.created_at)}
                  </TableCell>
                  <TableCell>
                    <Link
                      to="/sites/$siteSlug/forms/$formId/submissions"
                      params={{ siteSlug, formId: form.id }}
                      className="text-muted-foreground hover:text-foreground hover:underline"
                    >
                      {form.submission_count}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground"
                        asChild
                      >
                        <Link
                          to="/sites/$siteSlug/forms/$formId"
                          params={{ siteSlug, formId: form.id }}
                          title="Edit form"
                        >
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground hover:text-destructive"
                        title="Delete form"
                        onClick={async () => {
                          if (await confirmDialog(`Delete "${form.name}"?`, { destructive: true, confirmLabel: 'Delete form' })) {
                            handleDelete(form.id, form.name);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
